/*
 * Restock notify panel. Swaps in for the disabled sold-out submit inside
 * .product-form__buttons. A MutationObserver watches that submit button's
 * own `disabled` attribute (the exact node and attribute toggleAddButton in
 * global.js writes to on every variant change) instead of re-deriving
 * availability from a separate variant list, so the two can never disagree.
 */
(function () {
  if (customElements.get('abu-restock')) {
    return;
  }

  var TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var turnstileScript = null;
  var turnstileConfig = null;

  function loadTurnstileScript() {
    if (window.turnstile) {
      return Promise.resolve();
    }

    if (turnstileScript) {
      return turnstileScript;
    }

    var existing = document.querySelector('script[src="' + TURNSTILE_SRC + '"]');

    if (existing) {
      turnstileScript = new Promise(function (resolve) {
        existing.addEventListener('load', resolve, { once: true });
      });

      return turnstileScript;
    }

    turnstileScript = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return turnstileScript;
  }

  /**
   * The same site key and `required` flag the footer signup reads, from the
   * same endpoint. Fetched once per page and shared by every widget on it.
   */
  function getTurnstileConfig(api, region, language) {
    if (turnstileConfig) {
      return turnstileConfig;
    }

    turnstileConfig = fetch(
      api + '/footer?region=' + encodeURIComponent(region || '') + '&locale=' + encodeURIComponent(language || 'en'),
      { headers: { Accept: 'application/json' } }
    )
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (payload) {
        var data = payload && payload.data;
        var config = data && data.turnstile;

        return {
          siteKey: (config && config.siteKey) || '',
          required: Boolean(config && config.required),
        };
      })
      .catch(function () {
        return { siteKey: '', required: false };
      });

    return turnstileConfig;
  }

  class AbuRestock extends HTMLElement {
    connectedCallback() {
      var buttons = this.closest('.product-form__buttons');

      this.submitWrapper = buttons ? buttons.querySelector('[data-abu-submit-wrapper]') : null;
      this.watchedButton = buttons ? buttons.querySelector('.product-form__submit') : null;

      var form = document.getElementById('product-form-' + this.dataset.section);
      this.idInput = form ? form.querySelector('input[name="id"]') : null;

      this.row = this.querySelector('[data-abu-restock-row]');
      this.emailInput = this.querySelector('input[name="email"]');
      this.phoneInput = this.querySelector('input[name="phone"]');
      this.newsletterInput = this.querySelector('input[name="newsletter"]');
      this.submitButton = this.querySelector('[data-abu-restock-submit]');
      this.message = this.querySelector('[data-abu-restock-message]');
      this.checkmarkIcon = this.querySelector('[data-abu-restock-icon-checkmark]');
      this.errorIcon = this.querySelector('[data-abu-restock-icon-error]');
      this.turnstileSlot = this.querySelector('[data-abu-restock-turnstile]');
      this.turnstileId = null;
      this.turnstileToken = '';
      this.turnstileRequired = false;
      this.turnstileArmed = false;

      this.tabs = this.querySelectorAll('[data-abu-restock-tab]');
      this.panels = this.querySelectorAll('[data-abu-restock-panel]');
      this.activeTab = 'email';

      this.tabs.forEach(
        function (tab) {
          tab.addEventListener('click', this.onTabClick.bind(this, tab));
        }.bind(this)
      );

      if (this.submitButton) {
        this.submitButton.addEventListener('click', this.onSubmit.bind(this));
      }

      if (this.watchedButton) {
        this.observer = new MutationObserver(this.sync.bind(this));
        this.observer.observe(this.watchedButton, { attributes: true, attributeFilter: ['disabled'] });
      }

      this.sync();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    sync() {
      var unavailable = Boolean(this.watchedButton && this.watchedButton.disabled);

      this.hidden = !unavailable;

      if (this.submitWrapper) {
        this.submitWrapper.hidden = unavailable;
      }

      if (unavailable) {
        this.setMessage(null, '');
        this.armTurnstile();
      }
    }

    armTurnstile() {
      if (this.turnstileArmed || !this.turnstileSlot) {
        return;
      }

      this.turnstileArmed = true;

      var self = this;
      var api = this.dataset.api || 'https://api.abuniverse.com';

      getTurnstileConfig(api, this.dataset.region, this.dataset.language).then(function (config) {
        self.turnstileRequired = config.required;
        self.syncSubmitState();

        if (!config.required || !config.siteKey) {
          return;
        }

        return loadTurnstileScript().then(function () {
          self.turnstileId = window.turnstile.render(self.turnstileSlot, {
            sitekey: config.siteKey,
            size: 'flexible',
            appearance: 'interaction-only',
            language: (self.dataset.language || 'en').slice(0, 2),
            callback: function (token) {
              self.turnstileToken = token;
              self.syncSubmitState();
            },
            'expired-callback': function () {
              self.turnstileToken = '';
              self.syncSubmitState();
            },
            'error-callback': function () {
              self.turnstileToken = '';
              self.syncSubmitState();
            },
          });

          self.syncSubmitState();
        });
      });
    }

    syncSubmitState() {
      if (!this.submitButton) {
        return;
      }

      this.submitButton.disabled = this.turnstileRequired && !this.turnstileToken;
    }

    resetTurnstile() {
      this.turnstileToken = '';

      if (this.turnstileId !== null && window.turnstile) {
        window.turnstile.reset(this.turnstileId);
      }

      this.syncSubmitState();
    }

    currentVariantId() {
      return this.idInput && this.idInput.value ? Number(this.idInput.value) : null;
    }

    onTabClick(tab) {
      var target = tab.dataset.abuRestockTab;

      if (target === this.activeTab) {
        return;
      }

      this.activeTab = target;
      this.setMessage(null, '');

      this.tabs.forEach(function (t) {
        var active = t.dataset.abuRestockTab === target;
        t.classList.toggle('button--primary', active);
        t.classList.toggle('button--secondary', !active);
        t.setAttribute('aria-selected', String(active));
      });

      this.panels.forEach(function (panel) {
        panel.hidden = panel.dataset.abuRestockPanel !== target;
      });
    }

    digitsOnly(value) {
      return (value || '').replace(/\D/g, '');
    }

    onSubmit() {
      var id = this.currentVariantId();

      this.setMessage(null, '');

      if (!id) {
        return;
      }

      var body;

      if (this.activeTab === 'sms') {
        var digits = this.phoneInput ? this.digitsOnly(this.phoneInput.value) : '';

        if (digits.length !== 10) {
          return;
        }

        body = {
          phone: '+1' + digits,
          region: this.dataset.region,
          language: this.dataset.language,
          variant_id: String(id),
          company: '',
        };
      } else {
        var email = this.emailInput ? this.emailInput.value.trim() : '';

        if (!email) {
          return;
        }

        body = {
          email: email,
          region: this.dataset.region,
          language: this.dataset.language,
          variant_id: String(id),
          newsletter: Boolean(this.newsletterInput && this.newsletterInput.checked),
          company: '',
        };
      }

      this.setLoading(true);

      if (this.turnstileToken) {
        body.turnstile_token = this.turnstileToken;
      }

      fetch((this.dataset.api || 'https://api.abuniverse.com') + '/restock/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then(this.handleResponse.bind(this))
        .catch(this.handleFailure.bind(this))
        .then(
          function () {
            this.setLoading(false);
            this.resetTurnstile();
          }.bind(this)
        );
    }

    setLoading(isLoading) {
      if (!this.submitButton) {
        return;
      }

      this.submitButton.classList.toggle('loading', isLoading);
      this.submitButton.disabled = isLoading;

      var spinner = this.submitButton.querySelector('.loading-overlay__spinner');

      if (spinner) {
        spinner.classList.toggle('hidden', !isLoading);
      }
    }

    handleResponse(response) {
      if (response.status === 422) {
        return response.json().then(
          function (body) {
            this.setMessage('error', (body && body.message) || this.strings().failed);
          }.bind(this)
        );
      }

      if (!response.ok) {
        this.setMessage('error', this.strings().failed);
        return;
      }

      return response.json().then(
        function (body) {
          this.setMessage('success', this.outcomeText(body && body.status));
        }.bind(this)
      );
    }

    handleFailure() {
      this.setMessage('error', this.strings().failed);
    }

    outcomeText(status) {
      var strings = this.strings();
      var map = {
        confirmation_sent: strings.confirmationSent,
        subscribed: strings.subscribed,
        already_subscribed: strings.alreadySubscribed,
      };

      return map[status] || strings.failed;
    }

    strings() {
      return (
        window.abuRestockStrings || {
          confirmationSent: '',
          subscribed: '',
          alreadySubscribed: '',
          failed: '',
        }
      );
    }

    setMessage(kind, text) {
      if (!this.message) {
        return;
      }

      this.message.className = 'abu-restock__message form__message';
      this.message.innerHTML = '';

      if (!text) {
        this.message.hidden = true;
        if (this.row) {
          this.row.hidden = false;
        }
        return;
      }

      var icon = kind === 'success' ? this.checkmarkIcon : this.errorIcon;

      this.message.classList.add('abu-restock__message--' + kind);
      this.message.innerHTML = (icon ? icon.innerHTML : '') + '<span>' + text + '</span>';
      this.message.hidden = false;

      if (this.row) {
        this.row.hidden = kind === 'success';
      }
    }
  }

  customElements.define('abu-restock', AbuRestock);
})();
